function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

function encodeWavFromPcm(pcm: Float32Array, sampleRate: number): Blob {
  const bytesPerSample = 2;
  const numChannels = 1;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = pcm.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < pcm.length; i++) {
    const sample = Math.max(-1, Math.min(1, pcm[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function shouldPassThrough(blob: Blob): boolean {
  const base = (blob.type || '')
    .toLowerCase()
    .split(';')[0]
    .trim();
  return (
    base === 'audio/wav' ||
    base === 'audio/x-wav' ||
    base === 'audio/wave' ||
    base === 'audio/mpeg' ||
    base === 'audio/mp3'
  );
}

function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
}

function mixDownToMono(audioBuffer: AudioBuffer): Float32Array {
  const channels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const pcm = new Float32Array(length);
  for (let ch = 0; ch < channels; ch++) {
    const channel = audioBuffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      pcm[i] += channel[i] / channels;
    }
  }
  return pcm;
}

function resampleMono(pcm: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) {
    return pcm;
  }
  const outLength = Math.max(1, Math.floor((pcm.length * toRate) / fromRate));
  const out = new Float32Array(outLength);
  const ratio = fromRate / toRate;
  for (let i = 0; i < outLength; i++) {
    const srcIndex = Math.min(pcm.length - 1, Math.floor(i * ratio));
    out[i] = pcm[srcIndex];
  }
  return out;
}

async function decodeToMonoPcm(
  audioContext: AudioContext,
  blob: Blob
): Promise<{ pcm: Float32Array; sampleRate: number }> {
  const wav = await ensureWavForNota(blob);
  const arrayBuffer = await readBlobAsArrayBuffer(wav);
  const audioBuffer: AudioBuffer = await new Promise((resolve, reject) => {
    audioContext.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
  });
  return { pcm: mixDownToMono(audioBuffer), sampleRate: audioBuffer.sampleRate };
}

/**
 * NotaReport accepts WAV reliably; browser MediaRecorder outputs WebM/MP4.
 * Same conversion as the prescription app (`ensureWavForNota`).
 */
export async function ensureWavForNota(blob: Blob): Promise<Blob> {
  if (!blob || blob.size === 0) {
    throw new Error('Audio recording is empty.');
  }

  if (shouldPassThrough(blob)) {
    return blob;
  }

  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('This browser cannot convert audio to WAV.');
  }

  const audioContext = new AudioContextCtor();
  try {
    const arrayBuffer = await readBlobAsArrayBuffer(blob);
    const audioBuffer: AudioBuffer = await new Promise((resolve, reject) => {
      audioContext.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
    });
    return encodeWavFromPcm(mixDownToMono(audioBuffer), audioBuffer.sampleRate);
  } finally {
    if (audioContext.state !== 'closed') {
      await audioContext.close().catch(() => undefined);
    }
  }
}

/**
 * Join multiple recordings (for example after a reconnect) into one WAV.
 */
export async function mergeAudioBlobs(blobs: Blob[]): Promise<Blob> {
  const parts = (blobs || []).filter(blob => blob && blob.size > 0);
  if (!parts.length) {
    throw new Error('Audio recording is empty.');
  }
  if (parts.length === 1) {
    return ensureWavForNota(parts[0]);
  }

  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('This browser cannot convert audio to WAV.');
  }

  const audioContext = new AudioContextCtor();
  try {
    const decoded = [];
    for (let i = 0; i < parts.length; i++) {
      decoded.push(await decodeToMonoPcm(audioContext, parts[i]));
    }
    const sampleRate = decoded[0].sampleRate;
    const resampled = decoded.map(part => resampleMono(part.pcm, part.sampleRate, sampleRate));
    let total = 0;
    for (let i = 0; i < resampled.length; i++) {
      total += resampled[i].length;
    }
    const pcm = new Float32Array(total);
    let offset = 0;
    for (let i = 0; i < resampled.length; i++) {
      pcm.set(resampled[i], offset);
      offset += resampled[i].length;
    }
    return encodeWavFromPcm(pcm, sampleRate);
  } finally {
    if (audioContext.state !== 'closed') {
      await audioContext.close().catch(() => undefined);
    }
  }
}
